import { Fragment, useEffect, useState } from '@wordpress/element';
import { Button, TextControl } from '@wordpress/components';
import { useSettings } from '../App/CartContext';
import ProductSlider from './productSlider';
import FooterButtons from './FooterButtons';

const SideCart = ({ isOpen, onClose }) => {
  const { 
    settings, 
    cart, 
    fetchCart, 
    loading, 
    closeIconSvg, 
    trashIconSvg, 
    updateQty, 
    removeItem 
  } = useSettings();

  /* ---------------- PLUGIN VISIBILITY ---------------- */
  const plugin_enabled = settings?.atc_enable === 'true';
  const plugin_enabled_mobile = settings?.mobile_en === 'true';
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  
  if (!plugin_enabled) return null;
  
  if (isMobile && !plugin_enabled_mobile) return null;

  if (PSCFW_GLOBALS.hide_cart_ui) return null;

  /* ---------------- CART DETAILS VISIBILITY ---------------- */
  const showSubTotal = settings?.display_subtotal === 'true';
  const showShippingTotal = settings?.display_shipping_total === 'true';
  const showTotal = settings?.display_total === 'true';

  const HideFooter = !showSubTotal && !showShippingTotal && !showTotal;

  /* ---------------- PRODUCT CARD VISIBILITY ---------------- */
  const showProductImg = settings?.enable_pro_img === 'true';
  const showProductName = settings?.enable_pro_name === 'true';
  const showProductPrice = settings?.enable_pro_price === 'true';
  const showProductTotal = settings?.enable_pro_total === 'true';
  const showProductQty = settings?.enable_pro_qty === 'true';

  const [isRemoving, setIsRemoving] = useState(false);
  const shopUrl = window?.PSCFW_GLOBALS.shop_url;

  /* Fetch cart when sidecart opens */
  useEffect(() => {
    if (isOpen) {
      fetchCart();
    }
  }, [isOpen]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
  
    if (isOpen) {
      html.classList.add('pscfw-cart-open');
      body.classList.add('pscfw-cart-open');
    } else {
      html.classList.remove('pscfw-cart-open');
      body.classList.remove('pscfw-cart-open');
    }
  
    return () => {
      html.classList.remove('pscfw-cart-open');
      body.classList.remove('pscfw-cart-open');
    };
  }, [isOpen]);

  const headingJustifyMap = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  const closePositionStyle = {
    left: {
      left: '24px',
      right: 'auto',
    },
    right: {
      right: '24px',
      left: 'auto',
    },
  };

  return (
    <Fragment>
      {/* BACKDROP */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
          zIndex: 9998,
        }}
      />

      {/* SIDECART */}
      <aside
        onClick={(e) => e.stopPropagation()}
        className="sidecart-main"
        style={{
          width: settings?.atcmax_width + 'px',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s ease',
        }}
      >
        {/* HEADER */}
        <div
          style={{
            padding: 20,
            borderBottom: `1px ${settings?.shb_style} ${settings?.shborder_color}`,
            backgroundColor: settings?.shead_color,
            boxShadow: 'rgba(0,0,0,0.2) 0px 0px 6px',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <div 
            style={{ 
              fontSize: settings?.head_font_size + 'px', 
              fontWeight: 600, 
              display: 'flex',
              justifyContent: headingJustifyMap[settings?.header_heading_position] || 'flex-start', 
              color: settings?.shthead_color 
            }}
          >
            {settings?.cart_head_text} ({cart.count})
          </div>

          {settings.enable_header_close === 'true' &&
            <Button
              className="close-btn"
              onClick={onClose}
              style={{
                minWidth: 'auto',
                padding: 0,
                position: 'absolute',
                margin: 'auto',
                right: '24px',
                top: '0px',
                bottom: '0px',
                ...(closePositionStyle[settings?.header_close_position] || closePositionStyle.right),
              }}
            >
              <span
                dangerouslySetInnerHTML={{ __html: closeIconSvg }}
                className="close-svg"
                style={{
                  '--close-svg-size': settings?.head_close_size + 'px',
                  '--close-svg-color': settings?.close_icon_color,
                  display: 'inline-flex',
                  color: settings?.shthead_color,
                }}
              />
            </Button>
          }
        </div>

        {/* PRODUCTS */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: settings?.clpback_color,
            position: 'relative',
            padding: 16,
          }}
        >
          {(loading || isRemoving) && (
            <div className="overlay-loader">
              <div className="spinner"></div>
            </div>
          )}

          {cart.items.length === 0 ? (
            <div className="empty-cart-section">
              {settings?.empty_cart_text}
              <Button
                className="return-to-shop-btn"
                style={{
                  '--btn-font-size': settings?.btn_font_size + 'px',
                  '--btn-padding': settings?.pscfw_fbtn_padding,
                  '--btn-bg-clr': settings?.cbc_color,
                  '--btn-bg-hvr-clr': settings?.cbh_color,
                  '--btn-txt-clr': settings?.btn_text_color,
                  '--btn-txt-hvr-clr': settings?.btnh_color,
                }}
                onClick={() => {
                  window.location.href = shopUrl;
                }}
              >
                {settings?.return_shop_text}
              </Button>
            </div>
          ) : !showProductImg && !showProductName && !showProductPrice && !showProductTotal && !showProductQty ? (
            null
          ) : (
            cart.items.map((item) => (
              <div
                key={item.key}
                className="product-card"
              >
                {settings?.enable_pro_img === 'true' && (
                  settings?.enable_product_link === 'true' ? 
                    (
                      <a href={item.permalink} target="_blank" rel="noopener noreferrer">
                        <img
                          src={item.image}
                          alt={item.name}
                          style={{
                            width: settings?.clpimg_width + 'px',
                            height: settings?.clpimg_width + 'px',
                            borderRadius: settings?.pib_radious + 'px',
                            objectFit: 'cover',
                          }}
                        />
                      </a> 
                    ) : (
                      <img
                        src={item.image}
                        alt={item.name}
                        style={{
                          width: settings?.clpimg_width + 'px',
                          height: settings?.clpimg_width + 'px',
                          borderRadius: settings?.pib_radious + 'px',
                          objectFit: 'cover',
                        }}
                      />
                    )
                )}

                <div style={{ flex: 1 }}>

                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: settings?. enable_pro_name != 'true' ? 'flex-end' : 'space-between'}}>

                    {settings?.enable_pro_name === 'true' && (
                      <div 
                        style={{ 
                          fontWeight: 500,
                          '--title-color': settings?.ptc_color,
                          '--title-hover-color': settings?.pth_hover,
                        }}
                      >
                        {settings?.enable_product_link === 'true' ? 
                          (
                            <a className="product-title" href={item.permalink} target="_blank" rel="noopener noreferrer">
                              {item.name}
                            </a>
                          ) : (
                            <span className="product-title">
                              {item.name}
                            </span>
                          )
                        }
                      </div>
                    )}

                    {settings?.enable_pro_delete === 'true' &&
                      <Button
                        className="delete-btn"
                        onClick={() => removeItem(item.key)}
                        style={{
                          '--trash-icon-clr': settings?.pd_color,
                          '--trash-icon-hvr-clr': settings?.pdc_hover,
                          '--trash-text-clr': settings?.pscfw_remove_text_color,
                          '--trash-text-hvr-clr': settings?.pscfw_remove_text_hover_color,
                          minWidth: 'auto',
                          padding: 0,
                        }}
                      >
                        {settings?.pscfw_delete_type === 'pscfw_delete_icon' ? ( 
                          <span
                            dangerouslySetInnerHTML={{ __html: trashIconSvg }}
                            className="delete-svg"
                            style={{
                              display: 'inline-flex',
                            }}
                          />
                        ) : (
                          <span className="delete-text" style={{ fontSize: '14px' }}>
                            {settings?.delete_btn_text}
                          </span>
                        )}
                      </Button>
                    }
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'stretch' }}>
                    {settings?.enable_pro_price === 'true' &&
                      <div style={{ color: settings?.prop_color, fontWeight: 600, marginTop: 4 }}>
                        {settings?.currency}{Number(item.price).toFixed(2)}
                      </div>
                    }

                    {settings?.enable_pro_qty === 'true' &&
                      <div>
                        <TextControl
                          type="number"
                          value={item.qty}
                          min="1"
                          onChange={(value) => updateQty(item.key, value)}
                          style={{ width: '60px' }}
                        />
                      </div>
                    }

                    {settings?.enable_pro_total === 'true' &&
                      <div style={{ marginTop: 4, fontWeight: 600, color: settings.prop_color }}>
                        {settings?.currency}{Number(item.subtotal).toFixed(2)}
                      </div>
                    }
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <ProductSlider products={cart.sliderProducts} />

        {/* FOOTER TOTAL */}
        {!HideFooter &&
          <div className="cart-details-section">
            {/* Subtotal */}
            {showSubTotal &&
              <div className="cart-details-item">
                <span>
                  Subtotal{' '}
                  <span style={{ fontWeight: 400, fontSize: 14 }}>
                    ({cart.prdCount} {cart.prdCount === 1 ? 'item' : 'items'})
                  </span>
                </span>
                <span>{settings?.currency}{Number(cart.subtotal).toFixed(2)}</span>
              </div>
            }

            {/* Shipping */}
            {showShippingTotal &&
              <div className="cart-details-item">
                <span>Shipping</span>
                <span>
                  {Number(cart.shipping || 0) === 0
                    ? 'Free'
                    : `${settings?.currency}${Number(cart.shipping).toFixed(2)}`}
                </span>
              </div>
            }

            {/* Divider */}
            {(showTotal && (showShippingTotal || showSubTotal)) && (
              <div
                style={{
                  border: '1px dashed #4b4b4b',
                  backgroundColor: '#e1e1e1',
                }}
              />
            )}

            {/* Total */}
            {showTotal &&
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                <span>Total</span>
                <span>{settings?.currency}{Number(cart.total).toFixed(2)}</span>
              </div>
            }
          </div>
        }

        {/* CTA */}
        <FooterButtons
          pscfw_btn_row={settings.pscfw_btn_row}
          pscfw_button_position={settings.pscfw_button_position}
        />
      </aside>
    </Fragment>
  );
};

export default SideCart;
